/**
 * Login form component with 2FA and consent checking support
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UserLogin } from '@/services/api';
import { TwoFactorVerifyLogin } from '@/components/auth/TwoFactorVerifyLogin';
import { ReConsentModal } from '@/components/auth/ReConsentModal';
import { config } from '@/src/config';
import { normalizeEmail } from '@/utils/emailValidator';

interface LoginFormProps {
  onClose?: () => void;
  onForgotPassword?: () => void;
  onTwoFactorStateChange?: (isActive: boolean) => void;
}

interface ConsentStatus {
  requires_consent: boolean;
  terms_needs_update: boolean;
  hipaa_needs_update: boolean;
  terms_current_version?: string;
  hipaa_current_version?: string;
}

interface TwoFactorChallengeResponse {
  requires_2fa: boolean;
  phone_number: string;
  phone_number_masked: string;
  message: string;
  email: string;
  consent_status?: ConsentStatus;
  available_methods?: string[];  // Available 2FA methods from backend
  preferred_method?: string;  // User's preferred 2FA method
}

interface LoginSuccessResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type: string;
  user: any;
  consent_status?: ConsentStatus;
}

export function LoginForm({ onClose, onForgotPassword, onTwoFactorStateChange }: LoginFormProps) {
  const { completeLogin } = useAuth();
  const [formData, setFormData] = useState<UserLogin>({
    email: '',
    password: '',
  });
  const [show2FAStep, setShow2FAStep] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallengeResponse | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Consent management state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);
  const [pendingLoginData, setPendingLoginData] = useState<LoginSuccessResponse | null>(null);

  // SSO state
  const [ssoAvailable, setSsoAvailable] = useState(false);
  const [ssoCboId, setSsoCboId] = useState<number | null>(null);
  const [ssoForceSso, setSsoForceSso] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  // Check SSO availability when email changes
  useEffect(() => {
    const email = formData.email;
    if (!email || !email.includes('@')) {
      setSsoAvailable(false);
      return;
    }
    const domain = email.split('@')[1];
    if (!domain || domain.length < 3) return;

    const checkSso = async () => {
      try {
        const resp = await fetch(`${config.weaveinApi.baseUrl}/api/saml/check?domain=${encodeURIComponent(domain)}`);
        if (resp.ok) {
          const data = await resp.json();
          setSsoAvailable(data.sso_enabled);
          setSsoCboId(data.cbo_id || null);
          setSsoForceSso(data.force_sso || false);
          // Auto-redirect if force SSO is enabled
          if (data.force_sso && data.cbo_id) {
            window.location.href = `${config.weaveinApi.baseUrl}/api/saml/login?cbo_id=${data.cbo_id}`;
          }
        }
      } catch {
        // SSO check failed silently — password login remains available
      }
    };

    const timer = setTimeout(checkSso, 500); // debounce
    return () => clearTimeout(timer);
  }, [formData.email]);

  const handleSsoLogin = () => {
    if (!ssoCboId) return;
    setSsoLoading(true);
    window.location.href = `${config.weaveinApi.baseUrl}/api/saml/login?cbo_id=${ssoCboId}`;
  };

  // Notify parent when 2FA step is active (prevents modal from closing on overlay click)
  useEffect(() => {
    onTwoFactorStateChange?.(show2FAStep);
  }, [show2FAStep, onTwoFactorStateChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoading(true);

    try {
      // Get device token if exists (for trusted device bypass)
      const deviceToken = localStorage.getItem('device_token');

      // Attempt login
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(deviceToken && { 'X-Device-Token': deviceToken }),
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Check if 2FA is required
      if (data.requires_2fa) {
        // Show 2FA verification step
        setTwoFactorChallenge(data);
        setShow2FAStep(true);
      } else {
        // Direct login successful - check consent status
        handleLoginSuccess(data);
      }
    } catch (err: any) {
      setLoginError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (loginData: LoginSuccessResponse) => {
    // Check if consent update is required
    if (loginData.consent_status && loginData.consent_status.requires_consent) {
      // Store login data and show consent modal
      setPendingLoginData(loginData);
      setConsentStatus(loginData.consent_status);
      setShowConsentModal(true);
    } else {
      // No consent update needed - complete login immediately
      completeLogin(loginData.access_token, loginData.user, loginData.refresh_token, loginData.expires_in);
      if (onClose) onClose();
    }
  };

  const handleConsentComplete = () => {
    // Re-consent completed - now complete the login
    setShowConsentModal(false);

    if (pendingLoginData) {
      completeLogin(pendingLoginData.access_token, pendingLoginData.user, pendingLoginData.refresh_token, pendingLoginData.expires_in);
      setPendingLoginData(null);
      if (onClose) onClose();
    }
  };

  const handle2FASuccess = (
    token: string,
    user: any,
    consentStatusFromVerify?: ConsentStatus,
    refreshToken?: string,
    expiresIn?: number,
  ) => {
    // 2FA verification successful - check consent status. Carry the refresh
    // token through: without it the session cannot survive a 401 (TRUS-537).
    const loginData: LoginSuccessResponse = {
      access_token: token,
      refresh_token: refreshToken,
      expires_in: expiresIn,
      token_type: 'bearer',
      user: user,
      consent_status: consentStatusFromVerify
    };

    handleLoginSuccess(loginData);
  };

  const handle2FACancel = () => {
    // Go back to login form
    setShow2FAStep(false);
    setTwoFactorChallenge(null);
    setFormData({ email: '', password: '' });
  };

  const handleResendCode = async () => {
    if (!twoFactorChallenge) return;

    // Get device token if exists
    const deviceToken = localStorage.getItem('device_token');

    // Re-attempt login to get a new code
    const response = await fetch(`${config.weaveinApi.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(deviceToken && { 'X-Device-Token': deviceToken }),
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Failed to resend code');
    }

    // Update challenge with new code info
    if (data.requires_2fa) {
      setTwoFactorChallenge(data);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Canonicalize the email as it is entered, so every downstream step —
    // /login, the 2FA resend/verify calls that re-send this address, and the
    // userEmail passed to TwoFactorVerifyLogin — agrees on one spelling.
    // Normalizing only at submit would leave those later calls carrying the
    // raw value (TRUS-386). Never touch the password: whitespace and case are
    // significant there.
    setFormData(prev => ({
      ...prev,
      [name]: name === 'email' ? normalizeEmail(value) : value
    }));
  };

  // Show consent modal if consent update is required
  if (showConsentModal && consentStatus && pendingLoginData) {
    return (
      <ReConsentModal
        consentStatus={consentStatus}
        onComplete={handleConsentComplete}
        userEmail={formData.email}
        accessToken={pendingLoginData.access_token}
      />
    );
  }

  // Show 2FA verification screen if needed
  if (show2FAStep && twoFactorChallenge) {
    return (
      <TwoFactorVerifyLogin
        email={twoFactorChallenge.email}
        phoneNumberMasked={twoFactorChallenge.phone_number_masked}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
        onResendCode={handleResendCode}
        availableMethods={twoFactorChallenge.available_methods ? (twoFactorChallenge.available_methods as ('sms' | 'totp' | 'backup')[]) : undefined}
        preferredMethod={twoFactorChallenge.preferred_method as ('sms' | 'totp' | 'backup') | undefined}
      />
    );
  }

  // Show regular login form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                // Mobile keyboards capitalize the first letter by default, which
                // turns "kamea@..." into "Kamea@..." without the user noticing
                // (TRUS-386). The backend now matches case-insensitively; these
                // keep the typed value clean in the first place.
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm text-base min-h-[44px]"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm text-base min-h-[44px]"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <div className="text-sm">
              <button
                type="button"
                onClick={onForgotPassword}
                className="font-medium text-teal-600 hover:text-teal-500 underline"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {loginError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{loginError}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || ssoForceSso}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-v2accent hover:bg-v2accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[44px]"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          {/* SSO Login Button */}
          {ssoAvailable && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">or</span>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSsoLogin}
                  disabled={ssoLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[44px]"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  {ssoLoading ? 'Redirecting to SSO...' : 'Sign in with SSO'}
                </button>
              </div>
            </>
          )}

          {onClose && (
            <div className="text-center">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
